import React, { useState, useEffect, useCallback } from "react"
import { Link } from "gatsby"
import Blob3D from "./Blob3D"
import Wavelet from "./Wavelet"
import { TweaksPanel, TweakSection, TweakRadio } from "./TweaksPanel"

const THEME_KEY = "site-theme"

function useTweakState() {
  const [theme, setThemeRaw] = useState("dark")
  const [background, setBackground] = useState("blob")

  useEffect(() => {
    const saved = localStorage.getItem(THEME_KEY)
    if (saved === "light" || saved === "dark") setThemeRaw(saved)
  }, [])

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme)
  }, [theme])

  const setTheme = useCallback((v) => {
    setThemeRaw(v)
    localStorage.setItem(THEME_KEY, v)
  }, [])

  const setTweak = useCallback(
    (key, val) => {
      if (key === "theme") setTheme(val)
      else if (key === "background") setBackground(val)
    },
    [setTheme]
  )

  return [{ theme, background }, setTweak]
}

function Background({ kind }) {
  if (kind === "none") return null
  const Comp = kind === "wavelet" ? Wavelet : Blob3D
  return (
    <div className={`bg-layer bg-${kind}`} aria-hidden="true">
      <Comp key={kind} />
    </div>
  )
}

function ThemeToggle({ theme, setTheme }) {
  const next = theme === "dark" ? "light" : "dark"
  return (
    <button
      className="theme-toggle"
      onClick={() => setTheme(next)}
      aria-label={`Switch to ${next} theme`}
    >
      <span className={theme === "light" ? "active" : ""}>light</span>
      <span className={theme === "dark" ? "active" : ""}>dark</span>
    </button>
  )
}

const TABS = [
  { id: "about", label: "about", idx: "01", path: "/" },
  { id: "projects", label: "projects", idx: "02", path: "/projects" },
  { id: "posts", label: "posts", idx: "03", path: "/posts" },
]

function Tabs() {
  return (
    <nav className="tabs" role="tablist">
      {TABS.map((t) => (
        <Link
          key={t.id}
          to={t.path}
          role="tab"
          className="tab"
          activeClassName="active"
          partiallyActive={t.path !== "/"}
        >
          <span className="idx">{t.idx}</span>
          <span>{t.label}</span>
        </Link>
      ))}
    </nav>
  )
}

function Hero({ background }) {
  return (
    <section className="hero" aria-label="intro">
      <Background kind={background} />
      <div className="hero-text">
        <div className="prompt-line">
          <span className="sigil">❯</span>
          <span className="host">rdiaz.dev</span>
          <span className="cursor" aria-hidden="true" />
        </div>
        <div className="tagline">
          <span>ricardo díaz</span>
          <span className="sep">·</span>
          <span>software engineer</span>
          <span className="sep">·</span>
          <span>machine learning</span>
        </div>
      </div>
    </section>
  )
}

function StatusCorner({ activeTab, theme }) {
  return (
    <div className="status-corner" aria-label="status">
      <span className="k">buf</span>
      <span className="v">{activeTab}</span>
      <span className="dot">·</span>
      <span className="k">theme</span>
      <span className="v">{theme}</span>
    </div>
  )
}

export function Label({ children }) {
  return (
    <div className="label">
      <span className="lead">#</span>
      {children}
    </div>
  )
}

function activeTabFromPath(pathname) {
  if (pathname.startsWith('/projects')) return 'projects';
  if (pathname.startsWith('/posts')) return 'posts';
  return 'about';
}

export default function Layout({ children, location }) {
  const activeTab = activeTabFromPath(location?.pathname ?? '/');
  const [tweaks, setTweak] = useTweakState()
  const { theme, background } = tweaks
  const setTheme = (v) => setTweak("theme", v)
  const setBackground = (v) => setTweak("background", v)

  return (
    <>
      <div className="app-bg" aria-hidden="true" />
      <div className="shell">
        <ThemeToggle theme={theme} setTheme={setTheme} />
        <Hero background={background} />
        <Tabs />
        {children}
        <StatusCorner activeTab={activeTab} theme={theme} />
        <TweaksPanel title="Tweaks">
          <TweakSection label="theme">
            <TweakRadio
              label="mode"
              value={theme}
              onChange={setTheme}
              options={[
                { value: "light", label: "light" },
                { value: "dark", label: "dark" },
              ]}
            />
          </TweakSection>
          <TweakSection label="background">
            <TweakRadio
              label="style"
              value={background}
              onChange={setBackground}
              options={[
                { value: "blob", label: "blob" },
                { value: "wavelet", label: "wave" },
                { value: "none", label: "off" },
              ]}
            />
          </TweakSection>
        </TweaksPanel>
      </div>
    </>
  )
}
